import asioLogo from "../assets/asio-compatible-logo.png";
import vstLogo from "../assets/vst-compatible-logo.png";
import {ProductsApi} from "../api/ProductsApi";
import {useAsync} from "./useAsync";
import {common} from "../i18n";

// Other companies' trademarks our products' pages name, and the notices their owners ask for. Steinberg's ASIO and VST
// usage guidelines (the ASIO and VST 3 SDKs) want their Compatible logo and notice on every page about a product built
// with the SDK, not only in the footer, in its documentation, and on the legal pages. Avid and Apple ask for a line of
// text, and no logos. A page shows the ones its products' descriptions (or the documentation) mention.
export type Trademark = 'asio' | 'vst' | 'avid' | 'apple';

export const ALL_TRADEMARKS: Trademark[] = ['asio', 'vst', 'avid', 'apple'];

const MENTIONS: Record<Trademark, RegExp> = {
    asio: /\bASIO\b/,
    // VST, VST3, VST®.
    vst: /\bVST/,
    avid: /\b(AAX|Pro Tools|Avid)\b/,
    apple: /\b(Audio Units?|AU|Logic Pro|GarageBand|macOS|Mac)\b/
};

// The trademarks any of the HTML mentions, in ALL_TRADEMARKS' order.
export function trademarksIn(...html: (string | undefined)[]): Trademark[] {
    const text = html.filter(Boolean).join('\n');
    return ALL_TRADEMARKS.filter(x => MENTIONS[x].test(text));
}

// The logos as supplied (each carries its own trademark line), then the notices: Steinberg's ASIO one in the ASIO SDK
// license agreement's words (§ 3.1 k), the others in their owners' guidelines' words.
export function TrademarkNotices({marks, className = ''}: { marks: Trademark[], className?: string }) {
    if (!marks.length) {
        return null;
    }
    const t = common.trademarks;
    return (
        <aside className={`trademark-notices ${className}`} aria-label={t.label}>
            {(marks.includes('asio') || marks.includes('vst')) && (
                <div className="trademark-logos">
                    {marks.includes('asio') && <img src={asioLogo} alt={t.asioLogoAlt} width="474" height="443"/>}
                    {marks.includes('vst') && <img src={vstLogo} alt={t.vstLogoAlt} width="503" height="472"/>}
                </div>
            )}
            <div>
                {marks.map(x => <p key={x} className="mb-1">{t[x]}</p>)}
            </div>
        </aside>
    );
}

// For a page that names products by SKU without their descriptions (checkout, licenses, orders): the notices for the
// published ones among them. Nothing while the products load, or when they can't be.
export function TrademarkNoticesFor({skus, className}: { skus: (string | undefined)[], className?: string }) {
    const {data: products} = useAsync(() => ProductsApi.getPublished(), []);
    const wanted = skus.filter((x): x is string => !!x).map(x => x.toLowerCase());
    const named = products?.filter(x => wanted.includes(x.sku.toLowerCase())) ?? [];
    return <TrademarkNotices marks={trademarksIn(...named.map(x => x.description))} className={className}/>;
}
